import type { ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  ACCENT_SHADOW,
  BRAND,
  CARD_RADIUS,
  MINT_GLOW,
  MINT_GLOW_STRONG,
  accentGradient,
  surfaceGradient,
} from './tokens';
import { PageHelp } from '../help/PageHelp';
import { useModule } from './modules';
import { drift, sheen } from './motion';

/** A subtitle longer than this would not fit one line of the banner, so it goes below it. */
const SUBTITLE_LIMIT = 80;

const DOTS = 'radial-gradient(circle, rgba(255, 255, 255, 0.55) 1px, transparent 1.5px)';

const blob = {
  position: 'absolute',
  width: 256,
  height: 256,
  borderRadius: '50%',
  bgcolor: MINT_GLOW_STRONG,
  filter: 'blur(64px)',
  animation: `${drift} 14s ease-in-out infinite alternate`,
} as const;

/**
 * The banner every page opens with: a badge naming the module, the title, a one-line subtitle and
 * all of the page's actions, on the brand's charcoal with mint light behind it, as the taqon.ai
 * hero. A `contained` button is the page's primary action (mint); every other button is secondary
 * (translucent). The page's contextual help
 * (FRD-CORE-041) sits beside the title wherever the route has an entry.
 */
export function PageHeader({
  title,
  description,
  actions,
  leading,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
  /** Shown before the badge and title, such as an employee's picture. */
  leading?: ReactNode;
}) {
  const tone = useModule();
  const below = description !== undefined && description.length > SUBTITLE_LIMIT;
  const subtitle = description !== undefined && !below;
  // The badge names the module and never repeats the title.
  const badge = tone.name.toLowerCase() === title.toLowerCase() ? 'TAQON INFINITY' : tone.name;
  return (
    <>
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: `${String(CARD_RADIUS)}px`,
          p: { xs: 2, sm: 3, lg: 4 },
          minHeight: { lg: 172 },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          color: '#ffffff',
          backgroundImage: surfaceGradient(),
          backgroundSize: '160% 100%',
          animation: `${sheen} 16s ease-in-out infinite alternate`,
          boxShadow: '0 20px 40px -20px rgba(30, 32, 35, 0.7)',
          // The mint line under the hero.
          '&::after': {
            content: '""',
            position: 'absolute',
            left: 0,
            bottom: 0,
            width: '38%',
            height: 3,
            backgroundImage: `linear-gradient(90deg, ${BRAND.mint}, ${BRAND.mintLight}, transparent)`,
          },
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: 0.14,
            backgroundImage: DOTS,
            backgroundSize: '60px 60px',
          }}
        />
        <Box aria-hidden sx={{ ...blob, top: -96, right: -96 }} />
        <Box aria-hidden sx={{ ...blob, bottom: -96, left: -96, animationDelay: '-7s' }} />
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            alignItems: { lg: 'center' },
            justifyContent: { lg: 'space-between' },
            gap: 2,
          }}
        >
          <Box
            sx={{
              minWidth: 0,
              flex: { lg: 1 },
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 2, sm: 2.5 },
            }}
          >
            {leading}
            <Box sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.5,
                  py: 0.5,
                  mb: 1.5,
                  borderRadius: 999,
                  color: BRAND.mintLight,
                  bgcolor: MINT_GLOW,
                  border: `1px solid ${MINT_GLOW_STRONG}`,
                  backdropFilter: 'blur(4px)',
                  letterSpacing: '0.04em',
                  fontSize: { xs: 12, sm: 14 },
                  lineHeight: { xs: '16px', sm: '20px' },
                  fontWeight: 600,
                  '& svg': { width: 16, height: 16, strokeWidth: 2 },
                }}
              >
                {tone.icon}
                {badge}
              </Box>
              <Stack
                direction="row"
                spacing={0.5}
                sx={{ alignItems: 'center', '& .MuiIconButton-root': { color: '#ffffff' } }}
              >
                <Typography
                  component="h1"
                  sx={{
                    fontSize: { xs: 24, sm: 30 },
                    lineHeight: { xs: '32px', sm: '36px' },
                    fontWeight: 700,
                    letterSpacing: '-0.01em',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {title}
                </Typography>
                <PageHelp />
              </Stack>
              {subtitle && (
                <Typography sx={{ mt: 1, fontSize: { xs: 14, sm: 16 }, lineHeight: '24px' }}>
                  {description}
                </Typography>
              )}
            </Box>
          </Box>
          {actions !== undefined && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: { xs: 1, sm: 1.5 },
                justifyContent: { lg: 'flex-end' },
                maxWidth: { lg: '52%' },
                '&& .MuiButton-root': {
                  height: 46,
                  px: 2.5,
                  borderRadius: '12px',
                  fontSize: 16,
                  color: '#ffffff',
                  backgroundColor: 'rgba(255, 255, 255, 0.10)',
                  backgroundImage: 'none',
                  border: '1px solid rgba(255, 255, 255, 0.20)',
                  boxShadow: 'none',
                  filter: 'none',
                  backdropFilter: 'blur(4px)',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.20)',
                    transform: 'translateY(-2px)',
                  },
                  '&.Mui-disabled': { opacity: 0.6 },
                },
                '&& .MuiButton-contained': {
                  color: BRAND.onMint,
                  backgroundColor: BRAND.mint,
                  backgroundImage: accentGradient(),
                  borderColor: 'transparent',
                  boxShadow: ACCENT_SHADOW,
                  '&:hover': {
                    backgroundColor: BRAND.mint,
                    transform: 'translateY(-2px)',
                    filter: 'brightness(1.06)',
                    boxShadow: '0 16px 28px -10px rgba(82, 184, 130, 0.8)',
                  },
                },
                '& .MuiChip-root': {
                  color: '#ffffff',
                  bgcolor: 'rgba(255, 255, 255, 0.20)',
                  borderColor: 'rgba(255, 255, 255, 0.35)',
                },
              }}
            >
              {actions}
            </Box>
          )}
        </Box>
      </Box>
      {below && (
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      )}
    </>
  );
}

/** A titled surface for one block of a page. */
export function Section({
  title,
  actions,
  children,
}: {
  title?: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Paper sx={{ overflow: 'hidden' }}>
      {(title !== undefined || actions !== undefined) && (
        <Stack
          direction="row"
          spacing={2}
          sx={{
            px: 2.5,
            py: 1.5,
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          {title !== undefined && (
            <Typography variant="subtitle1" component="h2">
              {title}
            </Typography>
          )}
          {actions}
        </Stack>
      )}
      <Box sx={{ overflowX: 'auto' }}>{children}</Box>
    </Paper>
  );
}

export function LoadingState({ label }: { label: string }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
      <CircularProgress size={28} aria-label={label} />
    </Box>
  );
}

export function ErrorState({ message }: { message: string }) {
  return (
    <Alert severity="error" variant="outlined">
      {message}
    </Alert>
  );
}

/** What a list shows when it has no rows yet: a short, plain statement. */
export function EmptyState({ title, detail }: { title: string; detail?: string }) {
  return (
    <Box sx={{ py: 5, px: 3, textAlign: 'center' }}>
      <Typography variant="subtitle1">{title}</Typography>
      {detail !== undefined && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {detail}
        </Typography>
      )}
    </Box>
  );
}
